import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { callTool } from "../api";
import { type CampaignFilters } from "../campaignFilters";
import { StatusBlock } from "../components/StatusBlock";
import { EVENT_KEY } from "../constants";
import type { LoadState } from "../types";
import type { CompanyBrowserTarget } from "../company-browser/types";

const PAGE_LIMIT = 200;

export interface EventContactRow {
  contact_id: string;
  name: string;
  title?: string | null;
  email?: string | null;
  account_id?: string | null;
  account_name?: string | null;
  event_contact_no: number;
  event_contact_display?: string | null;
}

interface EventContactPage {
  results?: EventContactRow[];
  total?: number;
  next_cursor?: string | null;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function matchesQuery(row: EventContactRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [row.name, row.account_name, row.email, row.title, row.event_contact_display]
    .filter((part) => part && String(part).trim())
    .join(" ")
    .toLowerCase()
    .includes(needle);
}

function useEventContacts(campaignKey: string, reloadKey: number) {
  const [board, setBoard] = useState<LoadState<EventContactPage>>({ status: "loading" });
  const [rows, setRows] = useState<EventContactRow[]>([]);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const requestGen = useRef(0);

  const load = useCallback(async () => {
    const gen = ++requestGen.current;
    setSearching(true);
    setBoard({ status: "loading" });
    try {
      const collected: EventContactRow[] = [];
      let cursor: string | undefined;
      let reported = 0;
      const seenCursors = new Set<string>();
      for (let page = 0; page < 20; page += 1) {
        const data = await callTool<EventContactPage>("event_contact_list", {
          campaign_key: campaignKey || EVENT_KEY,
          limit: PAGE_LIMIT,
          cursor,
        });
        if (gen !== requestGen.current) return;
        collected.push(...(data.results ?? []));
        reported = typeof data.total === "number" ? data.total : collected.length;
        const next = data.next_cursor?.trim() || "";
        if (!next || seenCursors.has(next)) break;
        seenCursors.add(next);
        cursor = next;
      }
      if (gen !== requestGen.current) return;
      const ordered = [...collected].sort((a, b) => a.event_contact_no - b.event_contact_no);
      setRows(ordered);
      setTotal(reported);
      setBoard(
        ordered.length
          ? { status: "ready", data: { results: ordered, total: reported } }
          : { status: "empty", message: "No numbered event contacts." },
      );
    } catch (error) {
      if (gen !== requestGen.current) return;
      setRows([]);
      setTotal(0);
      setBoard({
        status: "error",
        message: error instanceof Error ? error.message : "event_contact_list failed",
      });
    } finally {
      if (gen === requestGen.current) setSearching(false);
    }
  }, [campaignKey]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  return { board, rows, total, searching };
}

export function ContactsScreen({
  campaignKey,
  query,
  reloadKey = 0,
  onSearchingChange,
  onOpenCompany,
  onOpenContact,
  selectedContactId,
}: {
  eventKey: string;
  campaignKey: string;
  actor: string;
  filters: CampaignFilters;
  query: string;
  reloadKey?: number;
  onSearchingChange?: (pending: boolean) => void;
  onOpenCompany?: (target: CompanyBrowserTarget) => void;
  onGoToBooth?: (booth: string, label: string, hall?: string) => void;
  sharedBoothIds?: ReadonlySet<string>;
  onOpenContact?: (row: { contact_id: string }) => void;
  selectedContactId?: string | null;
}) {
  const listKey = campaignKey.trim() || EVENT_KEY;
  const { board, rows, total, searching } = useEventContacts(listKey, reloadKey);
  const textQuery = useDebouncedValue(query.trim(), 200);
  const visible = useMemo(
    () => rows.filter((row) => matchesQuery(row, textQuery)),
    [rows, textQuery],
  );

  useEffect(() => {
    onSearchingChange?.(searching);
    return () => onSearchingChange?.(false);
  }, [searching, onSearchingChange]);

  const tableState =
    board.status === "ready" && visible.length === 0
      ? { status: "empty" as const, message: "No contacts match these filters." }
      : board;

  return (
    <section className="campaign-builder contacts-board">
      {tableState.status === "ready" || (tableState.status === "empty" && rows.length > 0) ? (
        <p className="muted rating-scroll-status">
          Showing {visible.length} of {total}
        </p>
      ) : null}
      {tableState.status !== "ready" && visible.length === 0 ? (
        <StatusBlock state={tableState} loadingLabel="Loading contacts…" />
      ) : (
        <div className="rating-table-wrap">
          <table className="rating-table">
            <thead>
              <tr>
                <th className="col-event-contact">Event Contact ID</th>
                <th className="col-name">Name</th>
                <th className="col-account">Account</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr
                  key={`${row.event_contact_no}-${row.contact_id}`}
                  className={
                    [onOpenContact ? "clickable-row" : "", selectedContactId === row.contact_id ? "row-selected" : ""]
                      .filter(Boolean)
                      .join(" ") || undefined
                  }
                  onClick={() => onOpenContact?.({ contact_id: row.contact_id })}
                >
                  <td className="col-event-contact">{row.event_contact_display || "—"}</td>
                  <td className="col-name">
                    <strong>{row.name || "—"}</strong>
                  </td>
                  <td className="col-account">
                    {row.account_id && onOpenCompany ? (
                      <button
                        type="button"
                        className="ghost"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenCompany({ account_id: row.account_id! });
                        }}
                      >
                        {row.account_name ?? "Open account"}
                      </button>
                    ) : (
                      (row.account_name ?? "—")
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
